import React, { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Header from "./User/helper/Header";
import LoggedInNavbar from "./Admin/helper/loggedInNavbar";
import { isAuthenticated } from "./Auth/helper";
import { getPublicCalendarDates } from "./Core";

// Fallback important dates for demo/development
const fallbackDates = [
  { date: "2025-01-01", title: "Kalpataru Day (New Year)" },
  { date: "2025-01-06", title: "Dadaji's Mahasamadhi Day" },
  { date: "2025-01-13", title: "Lohri" },
  { date: "2025-01-22", title: "Dadaji's Birthday" },
  { date: "2025-01-26", title: "Republic Day" },
  { date: "2025-02-21", title: "Ma's First Samadhi Experience" },
  { date: "2025-02-26", title: "Mahashivratri" },
  { date: "2025-03-01", title: "Sri Ramkrishna's Birthday (Tithi)" },
  { date: "2025-03-14", title: "Holi / Duli Vandana / Ma's Birthday (Tithi)" },
  { date: "2025-03-26", title: "Ma's Birthday" },
  { date: "2025-04-06", title: "Ram Navami" },
  { date: "2025-07-10", title: "Guru Purnima" },
  { date: "2025-08-09", title: "Raksha Bandhan" },
  { date: "2025-08-15", title: "Sri Aurobindo's Birthday / Independence Day" },
  { date: "2025-08-15", title: "Janmashtami / Aarti at Midnight" },
  { date: "2025-08-16", title: "Janmashtami Celebrations" },
  { date: "2025-08-27", title: "Ganesh Chaturthi" },
  { date: "2025-09-03", title: "Ma's First Experience of Nirvikalpa Samadhi" },
  { date: "2025-09-06", title: "Anant Chaturdashi" },
  { date: "2025-09-22", title: "First Day of Navratri" },
  { date: "2025-10-02", title: "Dussehra" },
  { date: "2025-10-06", title: "Sharad Purnima (Kojagiri)" },
  { date: "2025-10-21", title: "Diwali / Lakshmi Puja (lighting of lamps)" },
  { date: "2025-11-05", title: "Guru Nanak's Birthday / Raas Purnima" },
  { date: "2025-12-05", title: "Sri Aurobindo's Mahasamadhi Day" },
  { date: "2025-12-17", title: "Dadaji Heard the Lord's Flute" },
  { date: "2025-12-24", title: "Christmas Eve" },
  { date: "2025-12-25", title: "Christmas" },
  { date: "2025-12-31", title: "Ma's Mahasamadhi Day" },
];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year, month) {
  return new Date(year, month, 1).getDay();
}

function formatDate(date) {
  // Use local date components to avoid timezone shifting
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getEventTypeColor(eventType) {
  const colors = {
    festival: "#dc3545",
    holiday: "#28a745",
    special: "#ffc107",
    event: "#007bff",
  };
  return colors[eventType] || "#E34607";
}

function getEventTypeCssClass(eventType) {
  const classes = {
    festival: "calendar-cell-festival",
    holiday: "calendar-cell-holiday",
    special: "calendar-cell-special",
    event: "calendar-cell-event",
  };
  return classes[eventType] || "calendar-cell-important";
}

// Helper function to safely parse dates without timezone issues
function safeDateToString(dateInput) {
  if (typeof dateInput === "string") {
    // If it's already a string, check the format
    if (dateInput.includes("T")) {
      // ISO string, extract date part
      return dateInput.split("T")[0];
    } else if (dateInput.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Already in YYYY-MM-DD format
      return dateInput;
    }
  }

  // For other cases, create date object and format properly
  const date = new Date(dateInput);
  // Add timezone offset to avoid shifting to previous day
  const offsetDate = new Date(
    date.getTime() + date.getTimezoneOffset() * 60000
  );
  return offsetDate.toISOString().split("T")[0];
}

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CustomCalendar = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth()); // Current month
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear()); // Current year
  const [modalInfo, setModalInfo] = useState(null); // { day, event }

  const [monthKey, setMonthKey] = useState(0); // for animation
  const [calendarDates, setCalendarDates] = useState([]); // API dates
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch calendar dates from API
  useEffect(() => {
    const fetchCalendarDates = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await getPublicCalendarDates(
          currentYear,
          currentMonth + 1
        );

        if (result && Array.isArray(result)) {
          // Transform API data to match the expected format
          const transformedDates = result
            .map((item) => ({
              date: safeDateToString(item.date),
              title: item.title,
              description: item.description,
              eventType: item.eventType,
              isActive: item.isActive,
            }))
            .filter((item) => item.isActive); // Only show active dates

          setCalendarDates(transformedDates);
        } else if (result?.error) {
          console.warn("API error, using fallback dates:", result.error);
          setCalendarDates(fallbackDates);
          setError("Using offline calendar data");
        } else {
          setCalendarDates(fallbackDates);
          setError("Using offline calendar data");
        }
      } catch (err) {
        console.error("Failed to fetch calendar dates:", err);
        setCalendarDates(fallbackDates);
        setError("Using offline calendar data");
      } finally {
        setLoading(false);
      }
    };

    fetchCalendarDates();
  }, [currentYear, currentMonth]);

  const year = currentYear;
  const importantDates = calendarDates;

  const daysInMonth = getDaysInMonth(year, currentMonth);
  const firstDay = getFirstDayOfWeek(year, currentMonth);
  const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
  const prevMonthDays = getDaysInMonth(year, prevMonth);

  // Build calendar grid with prev/next month days
  const calendar = [];
  let day = 1 - firstDay;
  for (let week = 0; week < 6; week++) {
    const weekRow = [];
    for (let d = 0; d < 7; d++) {
      if (day > 0 && day <= daysInMonth) {
        const dateObj = new Date(year, currentMonth, day);
        const dateStr = formatDate(dateObj);
        const events = importantDates.filter((e) => e.date === dateStr);
        const event = events.length > 0 ? events[0] : null; // Keep primary event for rendering
        const allEvents = events; // Store all events for the modal
        weekRow.push({
          day,
          dateStr,
          event,
          allEvents,
          isCurrentMonth: true,
        });
      } else if (day <= 0) {
        // Previous month
        const prevDate = prevMonthDays + day;
        const dateObj = new Date(year, prevMonth, prevDate);
        const dateStr = formatDate(dateObj);
        weekRow.push({
          day: prevDate,
          dateStr,
          event: null,
          allEvents: [],
          isCurrentMonth: false,
        });
      } else {
        // Next month
        const nextDate = day - daysInMonth;
        const dateObj = new Date(year, nextMonth, nextDate);
        const dateStr = formatDate(dateObj);
        weekRow.push({
          day: nextDate,
          dateStr,
          event: null,
          allEvents: [],
          isCurrentMonth: false,
        });
      }
      day++;
    }
    calendar.push(weekRow);
  }

  function handlePrevMonth() {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }

    setMonthKey((k) => k + 1);
  }

  function handleNextMonth() {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }

    setMonthKey((k) => k + 1);
  }

  // Modal close handler
  const handleCloseModal = () => setModalInfo(null);

  // Animation & style CSS
  const styleTag = (
    <>
      <style>{`
        html, body {
          overflow-x: hidden;
          margin: 0;
          padding: 0;
        }
        @keyframes calendar-fall-in {
          0% { opacity: 0; transform: translateY(-40px) scale(0.95); }
          60% { opacity: 1; transform: translateY(8px) scale(1.03); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .calendar-fall-in {
          animation: calendar-fall-in 0.6s cubic-bezier(0.4,0,0.2,1) both;
        }
        .calendar-table {
          border-radius: 24px;
          box-shadow: 0 4px 32px #e346070a;
          overflow: hidden;
          width: 100% !important;
          max-width: 100% !important;
        }
        .calendar-cell {
          transition: background 0.18s, box-shadow 0.18s, border 0.18s, transform 0.18s;
          background: #fff;
        }
        .calendar-cell-important {
          background: #FFE5C2;
          color: #E34607;
          font-weight: 700;
        }
        .calendar-cell-festival {
          background: #FFE5E5;
          color: #dc3545;
          font-weight: 700;
        }
        .calendar-cell-holiday {
          background: #E5F5E5;
          color: #28a745;
          font-weight: 700;
        }
        .calendar-cell-special {
          background: #FFF9E5;
          color: #ffc107;
          font-weight: 700;
        }
        .calendar-cell-event {
          background: #E5F3FF;
          color: #007bff;
          font-weight: 700;
        }
        .calendar-cell-important:hover,
        .calendar-cell-festival:hover,
        .calendar-cell-holiday:hover,
        .calendar-cell-special:hover,
        .calendar-cell-event:hover {
          outline: 2px solid currentColor;
          transform: scale(1.03);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        .calendar-cell:hover {
          z-index: 2;
        }
        
        /* Responsive Design */
        .calendar-container {
          padding: 10px;
          width: 100%;
          max-width: 100vw;
          box-sizing: border-box;
        }
        
        /* Mobile Phones (up to 480px) */
        @media (max-width: 480px) {
          .calendar-title {
            font-size: 24px !important;
            margin-top: 16px !important;
            padding: 0 10px;
          }
          .calendar-navigation {
            margin-bottom: 16px !important;
          }
          .calendar-navigation span {
            font-size: 18px !important;
            margin: 0 16px !important;
          }
          .calendar-navigation button {
            font-size: 20px !important;
          }
          .calendar-table {
            border-spacing: 4px 2px !important;
            border-radius: 12px !important;
          }
          .calendar-table th {
            font-size: 12px !important;
            padding: 4px 2px !important;
            width: 35px !important;
            text-align: center !important;
          }
          .calendar-cell {
            width: 35px !important;
            height: 35px !important;
            font-size: 14px !important;
          }
          .calendar-day-content {
            gap: 2px !important;
          }
          .event-dot {
            width: 4px !important;
            height: 4px !important;
          }
        }
        
        /* Tablets (481px to 768px) */
        @media (min-width: 481px) and (max-width: 768px) {
          .calendar-title {
            font-size: 28px !important;
            margin-top: 20px !important;
          }
          .calendar-navigation {
            margin-bottom: 20px !important;
          }
          .calendar-navigation span {
            font-size: 20px !important;
            margin: 0 20px !important;
          }
          .calendar-navigation button {
            font-size: 22px !important;
          }
          .calendar-table {
            border-spacing: 8px 4px !important;
            border-radius: 16px !important;
          }
          .calendar-table th {
            font-size: 14px !important;
            padding: 6px 4px !important;
            width: 50px !important;
            text-align: center !important;
          }
          .calendar-cell {
            width: 50px !important;
            height: 50px !important;
            font-size: 16px !important;
          }
          .calendar-day-content {
            gap: 3px !important;
          }
          .event-dot {
            width: 5px !important;
            height: 5px !important;
          }
        }
        
        /* Small Laptops (769px to 1024px) */
        @media (min-width: 769px) and (max-width: 1024px) {
          .calendar-title {
            font-size: 32px !important;
            margin-top: 24px !important;
          }
          .calendar-navigation {
            margin-bottom: 24px !important;
          }
          .calendar-navigation span {
            font-size: 24px !important;
            margin: 0 24px !important;
          }
          .calendar-navigation button {
            font-size: 24px !important;
          }
          .calendar-table {
            border-spacing: 12px 6px !important;
            border-radius: 20px !important;
          }
          .calendar-table th {
            font-size: 16px !important;
            padding: 8px 6px !important;
            width: 65px !important;
            text-align: center !important;
          }
          .calendar-cell {
            width: 65px !important;
            height: 65px !important;
            font-size: 18px !important;
          }
          .calendar-day-content {
            gap: 4px !important;
          }
          .event-dot {
            width: 6px !important;
            height: 6px !important;
          }
        }
        
        /* Large screens (1025px and up) */
        @media (min-width: 1025px) {
          .calendar-title {
            font-size: 36px !important;
            margin-top: 32px !important;
          }
          .calendar-navigation {
            margin-bottom: 32px !important;
          }
          .calendar-navigation span {
            font-size: 28px !important;
            margin: 0 32px !important;
          }
          .calendar-navigation button {
            font-size: 28px !important;
          }
          .calendar-table {
            border-spacing: 18px 12px !important;
            border-radius: 24px !important;
          }
          .calendar-table th {
            font-size: 20px !important;
            padding: 12px 8px !important;
            width: 80px !important;
            text-align: center !important;
          }
          .calendar-cell {
            width: 80px !important;
            height: 80px !important;
            font-size: 24px !important;
          }
          .calendar-day-content {
            gap: 8px !important;
          }
          .event-dot {
            width: 8px !important;
            height: 8px !important;
          }
        }
        
        /* Modal Responsive Styles */
        .modal-date-title {
          font-size: 38px;
          font-weight: 700;
          color: #E34607;
          margin-bottom: 12px;
          text-align: center;
        }
        
        .modal-event-count {
          font-size: 14px;
          color: #666;
          margin-bottom: 16px;
          font-weight: 500;
          text-align: center;
        }
        
        .modal-event-title {
          font-size: 24px;
          color: #333;
          text-align: center;
          margin-bottom: 8px;
          font-weight: normal;
        }
        
        .modal-event-description {
          font-size: 16px;
          color: #666;
          text-align: center;
          line-height: 1.5;
          max-width: 400px;
          margin-bottom: 8px;
        }
        
        .modal-close-button {
          position: absolute;
          top: 18px;
          right: 24px;
          background: none;
          border: none;
          font-size: 32px;
          color: #E34607;
          cursor: pointer;
          z-index: 2;
          transition: color 0.2s, transform 0.2s;
        }
        
        .modal-close-button:hover {
          color: #d12e00;
          transform: scale(1.1);
        }
        
        /* Mobile Modal Styles */
        @media (max-width: 480px) {
          .modal-date-title {
            font-size: 24px !important;
            margin-bottom: 8px !important;
          }
          
          .modal-event-count {
            font-size: 12px !important;
            margin-bottom: 12px !important;
          }
          
          .modal-event-title {
            font-size: 18px !important;
            margin-bottom: 6px !important;
          }
          
          .modal-event-description {
            font-size: 14px !important;
            max-width: 260px !important;
            margin-bottom: 6px !important;
          }
          
          .modal-close-button {
            top: 12px !important;
            right: 16px !important;
            font-size: 24px !important;
          }
        }
        
        /* Tablet Modal Styles */
        @media (min-width: 481px) and (max-width: 768px) {
          .modal-date-title {
            font-size: 30px !important;
            margin-bottom: 10px !important;
          }
          
          .modal-event-count {
            font-size: 13px !important;
            margin-bottom: 14px !important;
          }
          
          .modal-event-title {
            font-size: 20px !important;
            margin-bottom: 7px !important;
          }
          
          .modal-event-description {
            font-size: 15px !important;
            max-width: 320px !important;
            margin-bottom: 7px !important;
          }
          
          .modal-close-button {
            top: 15px !important;
            right: 20px !important;
            font-size: 28px !important;
          }
        }
      `}</style>
    </>
  );

  return (
    <>
      {isAuthenticated() &&
      isAuthenticated().user?._id &&
      isAuthenticated().token ? (
        <LoggedInNavbar />
      ) : (
        <Header />
      )}
      {styleTag}
      <Modal
        open={!!modalInfo}
        onClose={handleCloseModal}
        aria-labelledby="calendar-event-modal-title"
        aria-describedby="calendar-event-modal-description"
      >
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            borderRadius: { xs: 2, sm: 3, md: 4 },
            boxShadow: 24,
            p: { xs: 3, sm: 4, md: 6 },
            minWidth: { xs: 280, sm: 320, md: 400 },
            maxWidth: { xs: "90vw", sm: "85vw", md: "600px" },
            maxHeight: { xs: "80vh", sm: "75vh", md: "70vh" },
            minHeight: { xs: 160, sm: 180, md: 200 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            outline: "none",
            overflowY: "auto",
          }}
        >
          <button
            onClick={handleCloseModal}
            className="modal-close-button"
            aria-label="Close"
          >
            ×
          </button>
          {modalInfo && (
            <>
              <div className="modal-date-title">
                {modalInfo.day} {months[currentMonth]} {year}
              </div>
              {modalInfo.events && modalInfo.events.length > 1 && (
                <div className="modal-event-count">
                  {modalInfo.events.length} events on this day
                </div>
              )}
              {modalInfo.events &&
                modalInfo.events.map((event, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom:
                        modalInfo.events.length > 1 ? "24px" : "16px",
                      paddingBottom:
                        modalInfo.events.length > 1 &&
                        index < modalInfo.events.length - 1
                          ? "16px"
                          : "0",
                      borderBottom:
                        modalInfo.events.length > 1 &&
                        index < modalInfo.events.length - 1
                          ? "1px solid #eee"
                          : "none",
                      width: "100%",
                    }}
                  >
                    <div
                      className="modal-event-title"
                      style={{
                        fontWeight:
                          modalInfo.events.length > 1 ? "600" : "normal",
                      }}
                    >
                      {event.title}
                    </div>
                    {event.description && (
                      <div className="modal-event-description">
                        {event.description}
                      </div>
                    )}
                    {event.eventType && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <div
                          style={{
                            display: "inline-block",
                            background: getEventTypeColor(event.eventType),
                            color: "white",
                            padding: "4px 12px",
                            borderRadius: "16px",
                            fontSize: "12px",
                            fontWeight: "600",
                            textTransform: "uppercase",
                          }}
                        >
                          {event.eventType}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </>
          )}
        </Box>
      </Modal>
      <div
        className="calendar-container"
        style={{
          minHeight: "80vh",
          width: "100vw",
          boxSizing: "border-box",
          background: "#fff",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          margin: 0,
          padding: "10px",
        }}
      >
        <h2
          className="calendar-title"
          style={{
            color: "#E34607",
            marginBottom: 8,
            textAlign: "center",
            fontSize: 36,
            marginTop: 32,
          }}
        >
          Important Dates Calendar
        </h2>
        {error && (
          <div
            style={{
              background: "#fff3cd",
              border: "1px solid #ffeaa7",
              color: "#856404",
              padding: "8px 16px",
              borderRadius: "6px",
              marginBottom: "16px",
              fontSize: "14px",
              textAlign: "center",
            }}
          >
            {error}
          </div>
        )}
        {loading && (
          <div
            style={{
              textAlign: "center",
              color: "#6c757d",
              marginBottom: "16px",
              fontSize: "14px",
            }}
          >
            <i className="fas fa-spinner fa-spin me-2"></i>
            Loading calendar dates...
          </div>
        )}
        <div
          className="calendar-navigation"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: 32,
            width: "100%",
          }}
        >
          <button
            onClick={handlePrevMonth}
            style={{
              fontSize: 28,
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#E34607",
            }}
          >
            &lt;
          </button>
          <span style={{ fontWeight: 600, fontSize: 28, margin: "0 32px" }}>
            {months[currentMonth]} {year}
          </span>
          <button
            onClick={handleNextMonth}
            style={{
              fontSize: 28,
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#E34607",
            }}
          >
            &gt;
          </button>
        </div>
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "0 10px",
            boxSizing: "border-box",
          }}
        >
          <table
            className="calendar-table"
            style={{
              width: "100%",
              maxWidth: "1100px",
              margin: "0 auto",
              borderCollapse: "separate",
              borderSpacing: "18px 12px",
              background: "#fff",
              fontSize: 22,
              boxShadow: "none",
            }}
          >
            <thead>
              <tr
                style={{
                  color: "#E34607",
                  fontWeight: 600,
                  fontSize: 20,
                  marginBottom: 16,
                  height: 48,
                }}
              >
                <th style={{ textAlign: "center", width: 80 }}>Sun</th>
                <th style={{ textAlign: "center", width: 80 }}>Mon</th>
                <th style={{ textAlign: "center", width: 80 }}>Tue</th>
                <th style={{ textAlign: "center", width: 80 }}>Wed</th>
                <th style={{ textAlign: "center", width: 80 }}>Thu</th>
                <th style={{ textAlign: "center", width: 80 }}>Fri</th>
                <th style={{ textAlign: "center", width: 80 }}>Sat</th>
              </tr>
            </thead>
            <tbody key={monthKey}>
              {calendar.map((week, i) => (
                <tr key={i}>
                  {week.map((cell, j) => {
                    const isImportant = cell.isCurrentMonth && cell.event;
                    const eventTypeClass = cell.event
                      ? getEventTypeCssClass(cell.event.eventType)
                      : "";
                    const eventColor = cell.event
                      ? getEventTypeColor(cell.event.eventType)
                      : "#333";

                    // Build tooltip from events (title + optional description)
                    const tooltipText = isImportant
                      ? (cell.allEvents || [])
                          .map((ev) =>
                            [ev.title, ev.description]
                              .filter(Boolean)
                              .join(": ")
                          )
                          .join("\n")
                      : undefined;

                    return (
                      <td
                        key={j}
                        className={`calendar-fall-in calendar-cell${
                          isImportant ? ` ${eventTypeClass}` : ""
                        }`}
                        title={tooltipText}
                        style={{
                          width: 80,
                          height: 80,
                          textAlign: "center",
                          background: isImportant ? undefined : "#fff", // Let CSS classes handle background
                          color: cell.isCurrentMonth
                            ? cell.event
                              ? eventColor
                              : "#333"
                            : "#bbb",
                          borderRadius: 12,
                          cursor: isImportant ? "pointer" : "default",
                          position: "relative",
                          verticalAlign: "top",
                          transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
                          zIndex: 1,
                          fontWeight: isImportant ? 700 : 400,
                          fontSize: 24,
                          padding: 0,
                          animationDelay: `${(i * 7 + j) * 0.04}s`,
                        }}
                        onClick={() =>
                          isImportant &&
                          setModalInfo({
                            day: cell.day,
                            events: cell.allEvents,
                          })
                        }
                      >
                        {cell ? (
                          <div
                            title={tooltipText}
                            className="calendar-day-content"
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "100%",
                              height: "100%",
                              gap: 8,
                            }}
                          >
                            <div>{cell.day}</div>
                            {isImportant && (
                              <div
                                style={{
                                  display: "flex",
                                  gap: "3px",
                                  flexWrap: "wrap",
                                  justifyContent: "center",
                                  maxWidth: "60px",
                                }}
                              >
                                {cell.allEvents &&
                                  cell.allEvents
                                    .slice(0, 3)
                                    .map((event, idx) => (
                                      <div
                                        key={idx}
                                        className="event-dot"
                                        style={{
                                          width:
                                            cell.allEvents.length > 1 ? 8 : 12,
                                          height:
                                            cell.allEvents.length > 1 ? 8 : 12,
                                          background: getEventTypeColor(
                                            event.eventType
                                          ),
                                          borderRadius: "50%",
                                          transition: "background 0.25s",
                                        }}
                                      />
                                    ))}
                                {cell.allEvents &&
                                  cell.allEvents.length > 3 && (
                                    <div
                                      style={{
                                        fontSize: "10px",
                                        color: eventColor,
                                        fontWeight: "bold",
                                        marginLeft: "2px",
                                      }}
                                    >
                                      +{cell.allEvents.length - 3}
                                    </div>
                                  )}
                              </div>
                            )}
                          </div>
                        ) : null}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default CustomCalendar;
